import { Link } from "react-router-dom";
import { Section, ToneChip, Meter, StatusChip, fmtL, fmtP, mLabel, BR, TONES } from "./BpKit";

const toneOf = (p) => (p == null ? null : p >= 95 ? "on_track" : p >= 80 ? "at_risk" : "behind");
const Empty = ({ text }) => <p className="text-sm text-slate-500">{text}</p>;

export const CategoryTable = ({ rows }) => (
  <Section tid="bp-category-progress-table" title="By product category" hint="Plan to date vs PO bookings">
    {!rows.length ? <Empty text="No category targets or bookings yet." /> : (
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={r.category} className="grid grid-cols-12 items-center gap-x-3 gap-y-1" data-testid={`bp-category-row-${i}`}>
            <div className="col-span-12 sm:col-span-3 text-sm font-medium truncate">{r.category}</div>
            <div className="col-span-12 sm:col-span-6 min-w-0"><Meter pct={r.pct} tone={toneOf(r.pct)} /></div>
            <div className="col-span-12 sm:col-span-3 sm:text-right num text-xs min-w-0"><b>{fmtL(r.booked)}</b> <span className="text-slate-500">/ {fmtL(r.target_to_date)}</span></div>
          </div>
        ))}
      </div>
    )}
  </Section>
);

export const CountsTable = ({ rows, months }) => (
  <Section tid="bp-counts-table" title="New leads and RFQs" hint="Count per month: plan / actual">
    <div className="overflow-x-auto">
      <table className="ax-table ax-freeze w-full">
        <thead><tr><th>Metric</th>{months.map((m) => <th key={m} className="text-right">{mLabel(m)}</th>)}<th className="text-right">FY</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.metric} data-testid={`bp-counts-row-${r.metric}`}>
              <td className="font-medium whitespace-nowrap">{r.metric === "leads" ? "New leads" : "RFQs / Pre-POs"}</td>
              {r.months.map((c) => (
                <td key={c.month} className="text-right num text-xs whitespace-nowrap">
                  <span className="text-slate-400">{c.plan || "·"}</span> / <b className={c.plan && c.actual < c.plan ? "text-rose-600" : ""}>{c.actual}</b>
                </td>
              ))}
              <td className="text-right num text-xs whitespace-nowrap"><span className="text-slate-400">{r.fy_target || "·"}</span> / <b>{r.actual}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Section>
);

export const TeamTable = ({ rows, onPick }) => (
  <Section tid="bp-team-leaderboard" title="Team vs plan" hint="Each person's own sales plan vs the POs they've booked">
    {!rows.length ? <Empty text="No sales plans or bookings yet." /> : (
      <div className="overflow-x-auto">
        <table className="ax-table w-full" data-testid="bp-team-leaderboard-table">
          <thead><tr><th>#</th><th>Salesperson</th><th>Plan</th><th className="text-right">FY plan</th><th className="text-right">Plan to date</th><th className="text-right">Booked</th><th className="text-right">Achieved</th><th className="text-right">Gap</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.user_id} data-testid={`bp-team-leader-row-${r.user_id}`}>
                <td className="num text-slate-500">{i + 1}</td>
                <td><button type="button" className="font-medium hover:text-primary text-left" onClick={() => onPick(r.user_id)} data-testid={`bp-team-leader-pick-${r.user_id}`}>{r.name}</button></td>
                <td><StatusChip s={r.plan_status} tid={`bp-team-leader-plan-${r.user_id}`} /></td>
                <td className="text-right num">{fmtL(r.fy_target)}</td>
                <td className="text-right num">{fmtL(r.target_to_date)}</td>
                <td className="text-right num font-semibold">{fmtL(r.booked)}</td>
                <td className="text-right num">{fmtP(r.pct)}</td>
                <td className="text-right num text-rose-600">{r.gap ? fmtL(r.gap) : "—"}</td>
                <td><ToneChip t={r.tone} tid={`bp-team-leader-tone-${r.user_id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);

export const AccountsTable = ({ rows }) => (
  <Section tid="bp-accounts" title="Account plan" hint="Planned accounts, plus the biggest unplanned accounts that booked POs">
    {!rows.length ? <Empty text="No account plans yet." /> : (
      <div className="overflow-x-auto max-h-[480px]">
        <table className="ax-table ax-freeze w-full" data-testid="bp-account-master-table">
          <thead><tr><th>Account</th><th>Branch</th><th>Class</th><th>Responsible</th><th className="text-right">Last FY</th><th className="text-right">FY plan</th><th className="text-right">Booked</th><th className="text-right">Achieved</th><th className="text-right">vs LY</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.account_id}-${r.branch}`} data-testid={`bp-account-row-${r.account_id}-${r.branch}`}>
                <td className="min-w-[180px]">{r.account_id ? <Link to={`/sales/accounts/${r.account_id}`} className="font-medium hover:text-primary">{r.account_name}</Link>
                  : <span className="font-medium">{r.account_name} <span className="text-[10px] text-amber-700">(not linked)</span></span>}
                  {r.unplanned && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 border border-amber-300 bg-amber-50 px-1 rounded-sm">Not in plan</span>}</td>
                <td className="text-xs whitespace-nowrap">{BR[r.branch] || r.branch}</td>
                <td className="text-xs">{r.category || "—"}{r.segment ? ` · ${r.segment === "new" ? "New" : "Repeat"}` : ""}</td>
                <td className="text-xs">{r.people.join(", ") || "—"}</td>
                <td className="text-right num">{fmtL(r.ly)}</td>
                <td className="text-right num">{fmtL(r.fy_target)}</td>
                <td className="text-right num font-semibold">{fmtL(r.booked)}</td>
                <td className="text-right num">{fmtP(r.pct)}</td>
                <td className={`text-right num ${r.growth == null ? "" : r.growth >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{r.growth == null ? "—" : `${r.growth > 0 ? "+" : ""}${r.growth}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);

const Act = ({ c }) => {
  if (!c.plan) return <span className="num text-xs text-slate-500">{c.done}</span>;
  const p = Math.round((100 * c.done) / c.plan);
  return <span className="num text-xs whitespace-nowrap" style={{ color: TONES[toneOf(p)][2] }}><b>{c.done}</b><span className="text-slate-400"> / {c.plan}</span></span>;
};

export const ActivityTable = ({ rows, acts }) => (
  <Section tid="bp-winning-sheet" title="Weekly activity scoreboard" hint="Done / weekly target for this BP week and last (Saturday to Friday). Open Weekly review for the full winning sheet.">
    {!rows.length ? <Empty text="No weekly activity targets yet. Set them in My sales plan." /> : (
      <div className="overflow-x-auto">
        <table className="ax-table w-full" data-testid="bp-winning-sheet-scoreboard-table">
          <thead><tr><th>Salesperson</th><th>Week of</th>{Object.values(acts).map((l) => <th key={l} className="text-right">{l}</th>)}</tr></thead>
          <tbody>
            {rows.flatMap((r) => r.weeks.map((w, i) => (
              <tr key={`${r.user_id}-${w.week_start}`} data-testid={`bp-activity-row-${r.user_id}-${i}`} className={i ? "text-slate-500" : ""}>
                <td className="font-medium">{i ? "" : r.name}</td>
                <td className="text-xs whitespace-nowrap">{w.key ? `${w.key} · ` : ""}{new Date(`${w.week_start}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{i ? "" : " (this week)"}</td>
                {Object.keys(acts).map((k) => <td key={k} className="text-right"><Act c={w.items[k]} /></td>)}
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);
